import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './AppIcon';
import { navigationItems, socialLinks, quickLinks } from '../constants/navigation';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                <Icon name="Code2" size={24} className="text-accent-foreground" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">DevCraft</h3>
                <p className="text-primary-foreground/70 text-sm">Portfolio</p>
              </div>
            </div>
            
            <p className="text-primary-foreground/80 mb-6 max-w-md leading-relaxed">
              Passionate full-stack developer crafting digital experiences that solve real-world problems. 
              Specializing in React, Node.js, and modern web technologies.
            </p>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-primary-foreground/10 rounded-lg flex items-center justify-center hover:bg-accent hover:scale-110 transition-all duration-300 group"
                  aria-label={social.name}
                >
                  <Icon 
                    name={social.icon} 
                    size={18} 
                    className="text-primary-foreground/70 group-hover:text-accent-foreground transition-colors duration-300" 
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Navigation</h4>
            <ul className="space-y-3">
              {navigationItems.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-primary-foreground/70 hover:text-primary-foreground hover:translate-x-1 transition-all duration-300 flex items-center space-x-2 group"
                  >
                    <Icon 
                      name="ArrowRight" 
                      size={14} 
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                    />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Resources</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href="#"
                    className="text-primary-foreground/70 hover:text-primary-foreground hover:translate-x-1 transition-all duration-300 flex items-center space-x-3 group"
                  >
                    <Icon 
                      name={link.icon} 
                      size={16} 
                      className="text-primary-foreground/50 group-hover:text-accent transition-colors duration-300" 
                    />
                    <span>{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 pt-8 border-t border-primary-foreground/20">
          <div className="max-w-2xl">
            <h4 className="font-semibold text-lg mb-4">Stay Updated</h4>
            <p className="text-primary-foreground/70 mb-6">
              Get notified about new projects, blog posts, and technical insights.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 bg-primary-foreground/10 border border-primary-foreground/20 rounded-lg text-primary-foreground placeholder-primary-foreground/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              />
              <button className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent/90 transition-colors duration-300 flex items-center justify-center space-x-2">
                <Icon name="Send" size={18} />
                <span>Subscribe</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/20">
        <div className="container mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="text-primary-foreground/60 text-sm">
              © {currentYear} DevCraft Portfolio. All rights reserved.
            </div>

            {/* Status */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse-soft"></div>
                <span className="text-primary-foreground/70 text-sm">Available for work</span>
              </div>
              
              <div className="text-primary-foreground/40">•</div>
              
              <div className="text-primary-foreground/60 text-sm">
                Built with React & Tailwind CSS
              </div>
            </div>

            {/* Back to Top */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="w-10 h-10 bg-primary-foreground/10 rounded-lg flex items-center justify-center hover:bg-accent hover:scale-110 transition-all duration-300 group"
              aria-label="Back to top"
            >
              <Icon 
                name="ArrowUp" 
                size={18} 
                className="text-primary-foreground/70 group-hover:text-accent-foreground transition-colors duration-300" 
              />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
